import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { LinkOff, Refresh } from "@mui/icons-material";

/**
 * Blocking modal shown when the counterpart analyst screen is not present in
 * the analysis room (Ably presence) — tagging without it produces events with
 * no usable timestamp. Closes itself as soon as the other screen joins.
 *
 * Pass `onDismiss` to offer "Use Anyway", which lets the analyst carry on with
 * a stale timestamp; without it the modal cannot be dismissed at all.
 */
const PeerNotConnectedWarning = ({
  open,
  title,
  detail,
  checking,
  onRetry,
  onDismiss,
}) => (
  <Dialog
    open={!!open}
    disableEscapeKeyDown
    PaperProps={{
      sx: {
        borderRadius: "12px",
        minWidth: 360,
        maxWidth: "92vw",
        border: "1px solid var(--border-1)",
      },
    }}
  >
    <DialogTitle
      sx={{
        backgroundColor: "rgba(180, 83, 9, 0.08)",
        borderBottom: "2px solid rgba(180, 83, 9, 1)",
        py: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <LinkOff sx={{ fontSize: 18, color: "rgba(180, 83, 9, 1)" }} />
        <Typography
          sx={{ fontSize: "15px", fontWeight: 700, color: "rgba(180, 83, 9, 1)" }}
        >
          {title}
        </Typography>
      </Stack>
    </DialogTitle>
    <DialogContent sx={{ py: 2 }}>
      <Typography sx={{ fontSize: "13px", color: "var(--text-2)", mt: 1 }}>
        {detail}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "var(--text-2)", mt: 1.5 }}>
        {onDismiss
          ? "Tagging is blocked until both screens are connected. Use Anyway continues without a live timestamp."
          : "Tagging is blocked until both screens are connected."}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
      {onDismiss && (
        <Button
          variant="outlined"
          size="small"
          onClick={onDismiss}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontSize: "12px",
            borderColor: "var(--border-1)",
            color: "var(--text-2)",
          }}
        >
          Use Anyway
        </Button>
      )}
      <Button
        variant="contained"
        size="small"
        disabled={!!checking}
        onClick={onRetry}
        startIcon={<Refresh sx={{ fontSize: "16px !important" }} />}
        sx={{
          textTransform: "none",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 700,
          backgroundColor: "rgba(180, 83, 9, 1)",
          "&:hover": { backgroundColor: "rgba(146, 64, 14, 1)" },
        }}
      >
        {checking ? "Checking…" : "Retry"}
      </Button>
    </DialogActions>
  </Dialog>
);

export default PeerNotConnectedWarning;
